// Debug stand-in for the CookieYes banner where CookieYes isn't configured: Layout.cshtml renders
// body[data-cookieyes-debug="true"] only with an empty CookieYes:SiteId (appsettings.Development.json -
// the CDN answers localhost with 402) and in Development or with ?debugcookies=true; otherwise
// "false" (Razor never drops a data- attribute, so the value is the flag, not its presence). With
// a site id it never shows - not even when an ad blocker stops CookieYes.
//
// It mimics CookieYes closely enough that nothing downstream can tell the difference - matched
// against CookieYes's own script.js/banner.js:
// - cookie "cookieyes-consent" = "consentid:<id>,consent:yes|no,action:yes,<slug>:yes|no,...",
//   365 days, path=/, SameSite=Strict - what ModulePermissions reads server-side;
// - "cookieyes_consent_update" on document, detail { accepted: [...slugs], rejected: [...] } -
//   what cookie-consent.ts reloads on;
// - "cookieyes_banner_load" on document, detail = getCkyConsent();
// - window.getCkyConsent(), window.revisitCkyConsent(), window.performBannerAction().
// Like CookieYes, the banner only shows while no consent has been given; after that a small
// fixed "revisit" button reopens it (as does revisitCkyConsent()).

import { COOKIE_NAME, readConsentCookie } from "./cookie-consent-cookie";

interface CkyConsent {
    activeLaw: string;
    categories: Record<string, boolean>;
    isUserActionCompleted: boolean;
    consentID: string;
    languageCode: string;
}

declare global {
    interface Window {
        getCkyConsent?: () => CkyConsent;
        revisitCkyConsent?: () => void;
        performBannerAction?: (action: "accept_all" | "accept_partial" | "reject") => void;
    }
}

const COOKIE_EXPIRY_DAYS = 365;
// The site's categories in CookieYes, in the order CookieYes writes them to the cookie.
const CATEGORIES = ["necessary", "functional", "analytics", "performance", "advertisement"];
const NECESSARY = "necessary";

const ROOT_CLASS = "cky-debug";

// Same as CookieYes's _ckyRandomString(32): base64 of 32 random alphanumerics, padding stripped.
function randomConsentId(): string {
    const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghiklmnopqrstuvwxyz";
    let id = "";
    for (let i = 0; i < 32; i++) {
        id += chars[Math.floor(Math.random() * chars.length)];
    }
    return btoa(id).replace(/=+$/, "");
}

function writeConsentCookie(accepted: Set<string>, rejectAll: boolean): void {
    const consentId = readConsentCookie().get("consentid") || randomConsentId();
    const parts = [`consentid:${consentId}`, `consent:${rejectAll ? "no" : "yes"}`, "action:yes"];
    for (const slug of CATEGORIES) {
        parts.push(`${slug}:${accepted.has(slug) ? "yes" : "no"}`);
    }
    const expires = new Date(Date.now() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toUTCString();
    // Host-only (no domain=) unlike CookieYes's root-domain cookie - fine for debugging, and
    // localhost won't accept a domain attribute anyway.
    const secure = location.protocol === "https:" ? "; secure" : "";
    document.cookie = `${COOKIE_NAME}=${parts.join(",")}; expires=${expires}; path=/; SameSite=Strict${secure}`;
}

function getConsent(): CkyConsent {
    const cookie = readConsentCookie();
    const categories: Record<string, boolean> = {};
    for (const slug of CATEGORIES) {
        categories[slug] = slug === NECESSARY || cookie.get(slug) === "yes";
    }
    return {
        activeLaw: "gdpr",
        categories,
        isUserActionCompleted: cookie.get("action") === "yes",
        consentID: cookie.get("consentid") ?? "",
        languageCode: document.documentElement.lang || "en",
    };
}

function dispatch(name: string, detail: unknown): void {
    document.dispatchEvent(new CustomEvent(name, { detail }));
}

// Debug-only UI: plain literals, deliberately not design tokens - it should look like a tool,
// not part of the site, and shouldn't ship in site.css/components.css to every visitor.
const STYLES = `
.${ROOT_CLASS} { position: fixed; right: 16px; bottom: 16px; z-index: 2147483647; width: min(360px, calc(100vw - 32px)); padding: 12px 14px; background: #1e1e1e; color: #f0f0f0; border: 2px dashed #ffb000; border-radius: 6px; font: 13px/1.4 ui-monospace, Consolas, monospace; box-shadow: 0 4px 16px rgb(0 0 0 / 0.4); }
.${ROOT_CLASS}[hidden], .${ROOT_CLASS}-revisit[hidden] { display: none; }
.${ROOT_CLASS}-revisit { position: fixed; right: 16px; bottom: 16px; z-index: 2147483647; padding: 6px 10px; background: #1e1e1e; color: #ffb000; border: 2px dashed #ffb000; border-radius: 6px; font: 12px/1.4 ui-monospace, Consolas, monospace; cursor: pointer; box-shadow: 0 4px 16px rgb(0 0 0 / 0.4); }
.${ROOT_CLASS}-revisit:hover { background: #333; }
.${ROOT_CLASS} strong { display: block; margin-bottom: 4px; color: #ffb000; }
.${ROOT_CLASS} p { margin: 0 0 8px; color: #bbb; }
.${ROOT_CLASS} label { display: flex; gap: 8px; align-items: center; margin: 2px 0; cursor: pointer; }
.${ROOT_CLASS} input:disabled + span { opacity: 0.6; }
.${ROOT_CLASS} .${ROOT_CLASS}__actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.${ROOT_CLASS} button { flex: 1 1 auto; padding: 6px 8px; background: #333; color: inherit; border: 1px solid #666; border-radius: 4px; font: inherit; cursor: pointer; }
.${ROOT_CLASS} button:hover { background: #444; }
.${ROOT_CLASS} button[data-action="all"] { background: #ffb000; color: #1e1e1e; border-color: #ffb000; }
`;

function createBanner(): { open: () => void; close: () => void; act: (mode: "all" | "custom" | "reject") => void } {
    const style = document.createElement("style");
    style.textContent = STYLES;
    document.head.append(style);

    const banner = document.createElement("div");
    banner.className = ROOT_CLASS;
    banner.setAttribute("role", "dialog");
    banner.setAttribute("aria-label", "Cookie consent (debug)");
    banner.hidden = true;

    const checkboxes = CATEGORIES.map((slug) => {
        const input = document.createElement("input");
        input.type = "checkbox";
        input.value = slug;
        input.disabled = slug === NECESSARY;
        const text = document.createElement("span");
        text.textContent = slug;
        const label = document.createElement("label");
        label.append(input, text);
        return { input, label };
    });

    const heading = document.createElement("strong");
    heading.textContent = "DEBUG - CookieYes not loaded";
    const note = document.createElement("p");
    note.textContent = "Stand-in banner: sets the same cookie and fires the same events.";

    const actions = document.createElement("div");
    actions.className = `${ROOT_CLASS}__actions`;
    const buttons: [string, "all" | "custom" | "reject"][] = [
        ["Accept all", "all"],
        ["Save selection", "custom"],
        ["Reject all", "reject"],
    ];
    for (const [text, mode] of buttons) {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = text;
        button.dataset.action = mode;
        button.addEventListener("click", () => act(mode));
        actions.append(button);
    }

    banner.append(heading, note, ...checkboxes.map(({ label }) => label), actions);

    // CookieYes's revisit-consent button equivalent: shown whenever the banner isn't.
    const revisit = document.createElement("button");
    revisit.type = "button";
    revisit.className = `${ROOT_CLASS}-revisit`;
    revisit.textContent = "Cookies (debug)";
    revisit.hidden = true;
    revisit.addEventListener("click", () => open());

    document.body.append(banner, revisit);

    // The checkboxes start out as the saved consent, so "custom" without opening the banner first
    // (performBannerAction("accept_partial")) keeps it rather than rejecting everything.
    function syncCheckboxes(): void {
        const { categories } = getConsent();
        for (const { input } of checkboxes) {
            input.checked = categories[input.value] ?? false;
        }
    }
    syncCheckboxes();

    function open(): void {
        syncCheckboxes();
        banner.hidden = false;
        revisit.hidden = true;
    }

    // Mirrors CookieYes's banner action: necessary is always accepted; "custom" takes the
    // checkboxes, "all"/"reject" ignore them.
    function act(mode: "all" | "custom" | "reject"): void {
        const accepted = new Set<string>();
        for (const { input } of checkboxes) {
            if (input.value === NECESSARY || mode === "all" || (mode === "custom" && input.checked)) {
                accepted.add(input.value);
            }
        }
        writeConsentCookie(accepted, mode === "reject");
        banner.hidden = true;
        revisit.hidden = false;
        dispatch("cookieyes_consent_update", {
            accepted: CATEGORIES.filter((slug) => accepted.has(slug)),
            rejected: CATEGORIES.filter((slug) => !accepted.has(slug)),
        });
    }

    function close(): void {
        banner.hidden = true;
        revisit.hidden = false;
    }

    return { open, close, act };
}

function activate(): void {
    console.info("[cookie-consent-debug] No CookieYes site id - using the debug banner");

    const { open, close, act } = createBanner();
    window.getCkyConsent = getConsent;
    window.revisitCkyConsent = open;
    window.performBannerAction = (action) => act(action === "accept_all" ? "all" : action === "accept_partial" ? "custom" : "reject");

    const consent = getConsent();
    if (consent.isUserActionCompleted) {
        close();
    } else {
        open();
    }
    dispatch("cookieyes_banner_load", consent);
}

export function initCookieConsentDebug(): void {
    if (document.body.dataset.cookieyesDebug === "true") {
        activate();
    }
}
